import { Button, Divider, HStack, Image, Skeleton, Text, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LogInOnly from "../../components/Auth/LogInOnly";
import MyIcon from "../../components/Icon/MyIcon";
import LargeTemplate from "../../components/Layout/LargeTemplate";
import Template from "../../components/Layout/Template";
import ImageModal from "../../components/Modals/ImageModal";
import useMe from "../../hooks/useMe";
import { colors } from "../../styles";
import { ICertificate, ILicense, IPharmacy, IUser } from "../../types";
import { forceLogout, statusToKor, typeToKor } from "../../utils";
import CreateCertificateModal from "./Modals/Certificate/CreateCertificateModal";
import DeleteCertificateModal from "./Modals/Certificate/DeleteCertificateModal";
import RejectedCertificateModal from "./Modals/Certificate/RejectedCertificateModal";
import CreateLicenseModal from "./Modals/License/CreateLicenseModal";
import DeleteLicenseModal from "./Modals/License/DeleteLicenseModal";
import RejectedLicenseModal from "./Modals/License/RejectedLicenseModal";
import CreatePharmacyModal from "./Modals/Pharmacy/CreatePharmacyModal";
import DeletePharmacyModal from "./Modals/Pharmacy/DeletePharmacyModal";
import RejectedPharmacyModal from "./Modals/Pharmacy/RejectedPharmacyModal";

export default function Me() {
    const { meData, meLoading } = useMe();
    const navigate = useNavigate();
    const toast = useToast();

    const [user, setUser] = useState<IUser | null>(null);
    const [certificate, setCertificate] = useState<ICertificate | undefined>(undefined);
    const [license, setLicense] = useState<ILicense | undefined>(undefined);
    const [pharmacy, setPharmacy] = useState<IPharmacy | undefined>(undefined);

    useEffect(() => {
        if (!meData) return;
        setUser(meData.user);
        setCertificate(meData.user.certificate);
        setLicense(meData.user.license);
        setPharmacy(meData.user.pharmacy);
    }, [meData]);

    const { isOpen: isImageModalOpen, onOpen: onOpenImageModal, onClose: onCloseImageModal } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImageModal();
    };

    const {
        isOpen: isCreateCertificateOpen,
        onOpen: onOpenCreateCertificate,
        onClose: onCloseCreateCertificate,
    } = useDisclosure();
    const {
        isOpen: isRejectedCertificateOpen,
        onOpen: onOpenRejectedCertificate,
        onClose: onCloseRejectedCertificate,
    } = useDisclosure();
    const {
        isOpen: isDeleteCertificateOpen,
        onOpen: onOpenDeleteCertificate,
        onClose: onCloseDeleteCertificate,
    } = useDisclosure();

    const {
        isOpen: isCreateLicenseOpen,
        onOpen: onOpenCreateLicense,
        onClose: onCloseCreateLicense,
    } = useDisclosure();
    const {
        isOpen: isRejectedLicenseOpen,
        onOpen: onOpenRejectedLicense,
        onClose: onCloseRejectedLicense,
    } = useDisclosure();
    const {
        isOpen: isDeleteLicenseOpen,
        onOpen: onOpenDeleteLicense,
        onClose: onCloseDeleteLicense,
    } = useDisclosure();

    const {
        isOpen: isCreatePharmacyOpen,
        onOpen: onOpenCreatePharmacy,
        onClose: onCloseCreatePharmacy,
    } = useDisclosure();
    const {
        isOpen: isRejectedPharmacyOpen,
        onOpen: onOpenRejectedPharmacy,
        onClose: onCloseRejectedPharmacy,
    } = useDisclosure();
    const {
        isOpen: isDeletePharmacyOpen,
        onOpen: onOpenDeletePharmacy,
        onClose: onCloseDeletePharmacy,
    } = useDisclosure();

    return (
        <LogInOnly>
            {!meLoading ? (
                <Template>
                    <Text fontSize={32} fontWeight={700}>마이페이지</Text>
                    <Divider marginY={5} />

                    <VStack width="100%" padding={5} alignItems="flex-start" spacing={5}>
                        <Text fontSize={18} fontWeight={700} width="100px">기본 정보</Text>
                        <VStack width="100%" justifyContent="flex-start" alignItems="center" spacing={5} marginTop={5}>
                            <InfoRow label="가입 유형" value={typeToKor(user?.type ?? null)} />
                            {/* <InfoRow label="상태" value={statusToKor(user?.status ?? null)} /> */}
                            <Divider marginY={5} />
                            <InfoRow label="이름" value={user?.name} />
                            <InfoRow label="이메일" value={user?.email} />
                            <InfoRow label="전화번호" value={user?.phone} />
                            <HStack width="100%" justifyContent="flex-start" alignItems="center" spacing={5}>
                                <Text fontSize={16} fontWeight={700} color={colors.g3} width="120px">비밀번호</Text>
                                <Button
                                    size="sm"
                                    variant="solid"
                                    backgroundColor={colors.g4}
                                    color={colors.black}
                                    onClick={() => navigate("/changepassword")}
                                >
                                    새 비밀번호
                                </Button>
                            </HStack>
                        </VStack>

                        <Divider marginY={5} />

                        {/* 약대생 정보: Certificate 연결 여부에 따라 생성/보완/삭제 */}
                        <SectionHeader title="약대생 정보">
                            <ObjectToolbar
                                status={certificate?.status}
                                onCreate={onOpenCreateCertificate}
                                onFix={onOpenRejectedCertificate}
                                onDelete={onOpenDeleteCertificate}
                            />
                        </SectionHeader>
                        {certificate ? (
                            <VStack width="100%" justifyContent="flex-start" alignItems="center" spacing={5} marginTop={5}>
                                <RejectedReasonText reason={certificate.rejectedReason} status={certificate.status} />
                                <ProofImageRow
                                    label="증명서"
                                    url={certificate.image}
                                    alt="certificateImage"
                                    onClick={openImage}
                                />
                            </VStack>
                        ) : (
                            <Text fontSize={16} fontWeight={500}>증명서가 없습니다.</Text>
                        )}

                        <Divider marginY={5} />

                        {/* 약사 정보: License 연결 여부에 따라 생성/보완/삭제. place는 다루지 않는다. */}
                        <SectionHeader title="약사 정보">
                            <ObjectToolbar
                                status={license?.status}
                                onCreate={onOpenCreateLicense}
                                onFix={onOpenRejectedLicense}
                                onDelete={onOpenDeleteLicense}
                            />
                        </SectionHeader>
                        {license ? (
                            <VStack width="100%" justifyContent="flex-start" alignItems="center" spacing={5} marginTop={5}>
                                <RejectedReasonText reason={license.rejectedReason} status={license.status} />
                                <InfoRow label="약사 면허번호" value={license.number} />
                                <ProofImageRow
                                    label="약사 면허증"
                                    url={license.image}
                                    alt="licenseImage"
                                    onClick={openImage}
                                />
                            </VStack>
                        ) : (
                            <Text fontSize={16} fontWeight={500}>약사 정보가 없습니다.</Text>
                        )}

                        <Divider marginY={5} />

                        {/* 약국 정보: Pharmacy 연결 여부. 생성은 면허가 있을 때만. */}

                        <SectionHeader title="약국 정보">
                            <ObjectToolbar
                                status={pharmacy?.status}
                                onCreate={() => {
                                    if (!license) {
                                        toast({
                                            title: "약사 정보를 먼저 등록해주세요.",
                                            status: "warning",
                                            position: "top",
                                            duration: 2000,
                                            isClosable: true,
                                        });
                                        return;
                                    }
                                    onOpenCreatePharmacy();
                                }}
                                onFix={onOpenRejectedPharmacy}
                                onDelete={onOpenDeletePharmacy}
                                createDisabled={!license || (license.status !== "ACTIVE")}
                            />
                        </SectionHeader>

                        {pharmacy ? (
                            <VStack width="100%" justifyContent="flex-start" alignItems="center" spacing={5} marginTop={5}>
                                <RejectedReasonText reason={pharmacy.rejectedReason} status={pharmacy.status} />
                                <InfoRow label="약국 이름" value={pharmacy.name} />
                                <InfoRow label="약국 주소" value={pharmacy.address} />
                                <InfoRow label="요양기관번호" value={pharmacy.healthcareInstitutionId} />
                                <InfoRow label="사업자등록번호" value={pharmacy.bizRegNo} />
                                <ProofImageRow
                                    label="사업자 등록증"
                                    url={pharmacy.businessRegistrationCertImage}
                                    alt="businessRegistrationCertImage"
                                    onClick={openImage}
                                />
                                <ProofImageRow
                                    label="설립 등록증"
                                    url={pharmacy.establishmentRegistrationCertImage}
                                    alt="establishmentRegistrationCertImage"
                                    onClick={openImage}
                                />
                            </VStack>
                        ) : (
                            <Text fontSize={16} fontWeight={500}>약국 정보가 없습니다.</Text>
                        )}
                    </VStack>

                    <HStack justifyContent="center" alignItems="center" paddingTop={10} width="100%">
                        <Button size="sm" variant="ghost" style={{ color: colors.g3 }} onClick={forceLogout}>
                            로그아웃
                        </Button>
                        <Button size="sm" variant="ghost" style={{ color: colors.g3 }} onClick={() => navigate("/quit")}>
                            탈퇴하기
                        </Button>
                    </HStack>

                    <ImageModal
                        isOpen={isImageModalOpen}
                        onClose={() => {
                            setSelectedImageUrl(null);
                            onCloseImageModal();
                        }}
                        imageUrl={selectedImageUrl ?? ""}
                    />

                    <CreateCertificateModal isOpen={isCreateCertificateOpen} onClose={onCloseCreateCertificate} />
                    {certificate && (
                        <RejectedCertificateModal
                            isOpen={isRejectedCertificateOpen}
                            onClose={onCloseRejectedCertificate}
                            certificate={certificate}
                        />
                    )}
                    <DeleteCertificateModal isOpen={isDeleteCertificateOpen} onClose={onCloseDeleteCertificate} />

                    <CreateLicenseModal isOpen={isCreateLicenseOpen} onClose={onCloseCreateLicense} />
                    {license && (
                        <RejectedLicenseModal
                            isOpen={isRejectedLicenseOpen}
                            onClose={onCloseRejectedLicense}
                            license={license}
                        />
                    )}
                    <DeleteLicenseModal isOpen={isDeleteLicenseOpen} onClose={onCloseDeleteLicense} />

                    <CreatePharmacyModal isOpen={isCreatePharmacyOpen} onClose={onCloseCreatePharmacy} />
                    {pharmacy && (
                        <RejectedPharmacyModal
                            isOpen={isRejectedPharmacyOpen}
                            onClose={onCloseRejectedPharmacy}
                            pharmacy={pharmacy}
                        />
                    )}
                    <DeletePharmacyModal isOpen={isDeletePharmacyOpen} onClose={onCloseDeletePharmacy} />
                </Template>
            ) : (
                <LargeTemplate>
                    <Skeleton height="32px" width="60%" />
                    <Skeleton height="32px" width="40%" />
                    <Skeleton height="32px" width="40%" />
                    <Skeleton height="32px" width="40%" />
                </LargeTemplate>
            )}
        </LogInOnly>
    );
}

function SectionHeader({ title, children }: { title: string; children: ReactNode }) {
    return (
        <HStack width="100%" justifyContent="space-between" alignItems="center">
            <Text fontSize={18} fontWeight={700} width="120px">{title}</Text>
            <HStack width="100%" justifyContent="flex-end" alignItems="center">
                {children}
            </HStack>
        </HStack>
    );
}

function InfoRow({ label, value }: { label: string; value?: string }) {
    return (
        <HStack width="100%" justifyContent="flex-start" alignItems="center" spacing={5}>
            <Text fontSize={16} fontWeight={700} color={colors.g3} width="120px">{label}</Text>
            <Text fontSize={16} fontWeight={500}>{value || "-"}</Text>
        </HStack>
    );
}

function ProofImageRow({
    label,
    url,
    alt,
    onClick,
}: {
    label: string;
    url?: string;
    alt: string;
    onClick: (url: string) => void;
}) {
    return (
        <HStack width="100%" justifyContent="flex-start" alignItems="flex-start" spacing={5}>
            <Text fontSize={16} fontWeight={700} color={colors.g3} width="120px">{label}</Text>
            {url ? (
                <Image
                    src={url}
                    alt={alt}
                    width={100}
                    height={125}
                    borderRadius={5}
                    objectFit="cover"
                    onClick={() => onClick(url)}
                    cursor="pointer"
                />
            ) : (
                <Text fontSize={16} fontWeight={500}>-</Text>
            )}
        </HStack>
    );
}

function RejectedReasonText({ reason, status }: { reason?: string; status?: string }) {
    if (status !== "REJECTED" || !reason) return null;
    return (
        <HStack width="100%" justifyContent="flex-start" alignItems="flex-start" spacing={5}>
            <Text fontSize={16} fontWeight={700} color={colors.g3} width="120px">반려 사유</Text>
            <Text fontSize={16} fontWeight={500} color={colors.apple}>{reason}</Text>
        </HStack>
    );
}

/* ACTIVE: 삭제 / REJECTED: 보완 / 없음: 생성 / PENDING: 검토 중 표시 */

function ObjectToolbar({
    status,
    onCreate,
    onFix,
    onDelete,
    createDisabled,
}: {
    status?: "ACTIVE" | "PENDING" | "REJECTED";
    onCreate: () => void;
    onFix: () => void;
    onDelete: () => void;
    createDisabled?: boolean;
}) {
    if (status === "ACTIVE") {
        return (
            <Button
                size="sm"
                borderRadius="full"
                variant="solid"
                backgroundColor={colors.g5}
                color={colors.black}
                onClick={onDelete}
            >
                <MyIcon src="/svg/trash-filled.svg" alt="trash" width={16} height={16} />
            </Button>
        );
    }

    if (status === "REJECTED") {
        return (
            <Button
                size="sm"
                variant="solid"
                backgroundColor={colors.black}
                color="white"
                onClick={onFix}
            >
                보완하기
            </Button>
        );
    }

    if (status === "PENDING") {
        return (
            <Text fontSize={14} fontWeight={500} color={colors.g3}>검토 중</Text>
        );
    }

    return (
        <Button
            size="sm"
            variant="unstyled"
            onClick={onCreate}
            opacity={createDisabled ? 0.5 : 1}
            disabled={createDisabled}
        >
            <MyIcon src="/svg/plus-circle.svg" alt="plus-circle" width={24} height={24} />
        </Button>
    );
}
